/**
 * App.jsx — routing, shell and global state.
 *
 * FIXES FROM THE ORIGINAL
 *
 * Real URLs.
 *   The original was a single component switching on a `view` string. Nothing
 *   had an address, so: the browser Back button did nothing (or left the app),
 *   nothing was bookmarkable, and — the big one — `shareItem()` shared
 *   `window.location.href`, which was the same URL for every post on the site.
 *   Every "share" produced a link to the homepage. Real routes fix all of it.
 *
 * 404 and error handling.
 *   An unknown `view` string rendered nothing: a blank white page with a
 *   header. There is now a real NotFound route, an ErrorBoundary around the
 *   whole tree, and a per-route boundary so one broken page does not take down
 *   navigation.
 *
 * Route guards.
 *   Signed out, signed in but unverified, and signed in but not onboarded are
 *   now three distinct, handled states instead of undefined behaviour.
 *
 * Code splitting.
 *   Every page is lazy-loaded. The original shipped one ~4,000-line bundle, so
 *   a clinician on hospital wifi waited for the messaging UI before they could
 *   read a single card.
 *
 * The reels feature has been removed entirely, as requested — no route, no
 * component, no upload path, no Storage rules for video.
 */

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, Link, NavLink,
  useLocation, useNavigate,
} from 'react-router-dom';
import {
  Newspaper, Users, MessageCircle, Bell, Search as SearchIcon,
  Sun, Moon, WifiOff, Settings as SettingsIcon, User, LogOut, Bookmark,
  Shield, HelpCircle, LifeBuoy, CalendarDays, Sparkles, BarChart3,
} from 'lucide-react';

import { doc, updateDoc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { LEGAL_VERSION } from './legal/documents';
import ConsentManager from './components/ConsentBanner';
import { useReadingStats, londonDay } from './hooks/useReadingStats';
import { recordVisit, useDot } from './lib/navDots';
import { applyRouteRules, setPageMeta } from './lib/pageMeta';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useNotifications } from './hooks/useSocial';
import { useConversations } from './hooks/useMessages';
import {
  ToastProvider, ErrorBoundary, Avatar, Button, PostSkeleton, useToast,
} from './components/ui';
import Menu, { MENU_CSS } from './components/Menu';
import { VERIFICATION_CSS } from './components/Verification';
// Reaction and poll styling is global rather than page-injected: PostCard
// renders on the feed, the profile and the detail page, and many times per
// page, so a <style> per card would be dozens of duplicate nodes.
import { REACTIONS_CSS } from './components/Reactions';
import { POST_EXTRAS_CSS } from './components/PostExtras';
// Global rather than page-injected: the suggestions card appears on the feed
// and will appear on the profile and search pages too.
import { SUGGESTED_CSS } from './components/SuggestedPeople';
// Article typography is global: an article renders as a card in the feed, on
// the profile, in search results and in full on its detail page. Only the
// long-form EDITOR's styling is page-local, because that page is lazy-loaded
// and most visitors never open it.
import { ARTICLE_CSS } from './components/ArticleBody';
import { NOT_BUILT_CSS } from './components/NotBuiltYet';
import { LOCATION_PICKER_CSS } from './components/LocationPicker';
import {
  applyTheme, currentTheme, watchSystemTheme, onThemeChange, THEME_CSS,
} from './lib/theme';
import { ONBOARDING_CSS } from './pages/Onboarding';
import { ASK_CSS } from './pages/Ask';

import './app.css';

/**
 * Styles that live next to their component.
 *
 * Some components ship their own CSS as an exported string rather than a
 * separate file — Menu, the verification badge, the onboarding wizard. That is
 * deliberate: those are the pieces where the CSS and the JS have to agree about
 * z-index and positioning, and keeping them in one file means they cannot drift
 * apart the way the original's four dropdown implementations did.
 *
 * Injected once at the root, before first paint.
 */
function GlobalStyles() {
  return (
    <style
      // eslint-disable-next-line react/no-danger — a build-time constant, not user input.
      dangerouslySetInnerHTML={{
        __html: [
          THEME_CSS, MENU_CSS, VERIFICATION_CSS, NOT_BUILT_CSS,
          REACTIONS_CSS, POST_EXTRAS_CSS, SUGGESTED_CSS, ARTICLE_CSS,
          LOCATION_PICKER_CSS, ONBOARDING_CSS, ASK_CSS,
        ].join('\n'),
      }}
    />
  );
}

/* ── Lazy routes ───────────────────────────────────────────────────────────── */

const Briefing     = lazy(() => import('./pages/Briefing'));
const Social       = lazy(() => import('./pages/Social'));
const PostDetail   = lazy(() => import('./pages/PostDetail'));
const Messages     = lazy(() => import('./pages/Messages'));
const Profile      = lazy(() => import('./pages/Profile'));
const ProfileById  = lazy(() => import('./pages/Profile').then((m) => ({ default: m.ProfileById })));
const Notifications= lazy(() => import('./pages/Notifications'));
const Library      = lazy(() => import('./pages/Library'));
const Search       = lazy(() => import('./pages/Search'));
const Ask          = lazy(() => import('./pages/Ask'));
const WriteArticle = lazy(() => import('./pages/WriteArticle'));
const Groups      = lazy(() => import('./pages/Groups'));
const GroupDetail = lazy(() => import('./pages/GroupDetail'));
const Events      = lazy(() => import('./pages/Events'));
const Settings     = lazy(() => import('./pages/Settings'));
const Report       = lazy(() => import('./pages/Report'));
const Onboarding   = lazy(() => import('./pages/Onboarding'));
const SignIn       = lazy(() => import('./pages/SignIn'));
const Landing      = lazy(() => import('./pages/Landing'));
const VerifyEmail  = lazy(() => import('./pages/VerifyEmail'));
const Legal        = lazy(() => import('./pages/Legal'));
const Verify       = lazy(() => import('./pages/Verify'));
const Support      = lazy(() => import('./pages/Support'));
const AdminVerify  = lazy(() => import('./pages/AdminVerification'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const AdminPlus    = lazy(() => import('./pages/AdminPlus'));
const AdminResearch = lazy(() => import('./pages/AdminResearch'));

/* ── 404 ───────────────────────────────────────────────────────────────────── */

export function NotFound() {
  const location = useLocation();

  return (
    <main className="notfound">
      {/* Announced to assistive tech; the visual 404 alone tells them nothing. */}
      <p className="notfound__code" aria-hidden="true">404</p>
      <h1>We could not find that page</h1>
      <p>
        The link may be broken, or the post may have been deleted by its author.
        {import.meta.env.DEV && <><br /><code>{location.pathname}</code></>}
      </p>
      <div className="notfound__actions">
        <Button onClick={() => window.history.back()}>Go back</Button>
        <Link to="/" className="btn btn--secondary btn--md">Today's briefing</Link>
        <Link to="/search" className="btn btn--ghost btn--md">Search</Link>
      </div>
    </main>
  );
}

/* ── Route guards ──────────────────────────────────────────────────────────── */

function FullPageLoader({ label = 'Loading' }) {
  return (
    <div className="container page" aria-busy="true" aria-live="polite" style={{ paddingTop: 24 }}>
      <span className="sr-only">{label}</span>
      <PostSkeleton />
      <PostSkeleton />
    </div>
  );
}

function RequireAuth({ children }) {
  const { loading, isSignedIn, needsOnboarding, isSuspended } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageLoader />;

  if (!isSignedIn) {
    // The front door gets a page that says what Clincordant is. Sending every
    // signed-out visitor at / straight to a login form told people nothing,
    // and left search engines nothing to index but the form.
    if (location.pathname === '/') return <Landing />;
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (isSuspended) {
    return (
      <main className="notfound">
        <Shield size={40} aria-hidden="true" />
        <h1>Your account is suspended</h1>
        <p>
          This usually follows a Community Guidelines breach. If you believe this is
          a mistake, contact <a href="mailto:support@clincordant.com">support@clincordant.com</a>.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/legal/community" className="btn btn--secondary btn--md">Read the guidelines</Link>
          {/* Reachable from here specifically. A suspended account is one of
              the few states where every other route is closed. */}
          <Link to="/support" className="btn btn--ghost btn--md">Contact us</Link>
        </div>
      </main>
    );
  }

  if (needsOnboarding && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />;
  }

  return children;
}

/* ── Header ────────────────────────────────────────────────────────────────── */

function Header({ dots }) {
  const { profile, uid, logout, isModerator } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [theme, setTheme] = useState(currentTheme);
  const { unreadCount } = dots;

  /**
   * Keep following the OS while the preference is 'system'.
   *
   * Someone on automatic evening dark mode picks "Match device" once and then
   * sits in whatever mode was current at that moment until they reload —
   * which reads as the setting being broken rather than as a missing listener.
   */
  useEffect(() => {
    const stop = watchSystemTheme();
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    const sync = () => setTheme(currentTheme());
    mq?.addEventListener('change', sync);
    // Settings → Appearance changes the same thing this icon reports. Without
    // this the icon kept showing the old mode until the header remounted.
    const unsub = onThemeChange((s) => setTheme(s.mode));
    return () => { stop(); unsub(); mq?.removeEventListener('change', sync); };
  }, []);

  // The header toggle is a quick light/dark flip. It deliberately writes an
  // explicit preference rather than cycling through 'system' — someone reaching
  // for this button wants the mode they can see, now. "Match device" lives in
  // Settings, where choosing it is a considered act.
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
    // Paint first, persist second. The write is fire-and-forget: a failed sync
    // must never stop the theme changing on the device in front of you, and the
    // local cache has already taken it.
    if (uid) {
      updateDoc(doc(db, 'users', uid), { themePref: next, updatedAt: serverTimestamp() })
        .catch(() => {});
    }
  };

  return (
    <header className="header">
      <div className="header__inner">
        <Link to="/" className="header__logo">Clincordant</Link>

        <Link to="/search" className="icon-btn" aria-label="Search">
          <SearchIcon size={19} />
        </Link>

        <nav className="header__nav" aria-label="Main">
          <NavLink to="/" end className="icon-btn nav-dot-host" aria-label={dots.briefing ? 'Briefing, ready to read' : 'Briefing'}>
            <Newspaper size={19} />
            {dots.briefing && <span className="nav-dot" aria-hidden="true" />}
          </NavLink>
          <NavLink to="/feed" className="icon-btn" aria-label="Social feed"><Users size={19} /></NavLink>
          <NavLink to="/ask" className="icon-btn" aria-label="Ask"><HelpCircle size={19} /></NavLink>
          <NavLink to="/messages" className="icon-btn" aria-label="Messages"><MessageCircle size={19} /></NavLink>
          <NavLink to="/notifications" className="icon-btn nav-dot-host" aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}>
            <Bell size={19} />
            {dots.alerts && <span className="nav-dot" aria-hidden="true" />}
          </NavLink>
        </nav>

        <div className="spacer" />

        <button type="button" className="icon-btn" onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <Menu
          align="end"
          label="Account menu"
          items={[
            { id: 'profile', label: 'Your profile', icon: <User size={16} />,
              onSelect: () => navigate(`/u/${profile?.usernameLower}`) },
            { id: 'plus', label: 'Clincordant Plus', icon: <Sparkles size={16} className="menu-plus-icon" />,
              onSelect: () => navigate('/settings/plan') },
            { id: 'library', label: 'Library', icon: <Bookmark size={16} />,
              onSelect: () => navigate('/library') },
            /**
             * Groups and events live here rather than in the bottom tab bar.
             * That bar has five slots and they are all load-bearing — adding a
             * sixth shrinks every tap target on the surface people use most, to
             * promote two features nobody has joined anything in yet.
             */
            { id: 'groups', label: 'Groups', icon: <Users size={16} />,
              onSelect: () => navigate('/groups') },
            { id: 'events', label: 'Events', icon: <CalendarDays size={16} />,
              onSelect: () => navigate('/events') },
            { id: 'settings', label: 'Settings', icon: <SettingsIcon size={16} />,
              onSelect: () => navigate('/settings') },
            /**
             * This pointed at /moderation, which has never been a route — so
             * the one link to the report queue landed on the 404 page. Reports
             * were being filed correctly the whole time and there was simply
             * nothing that could open them.
             */
            isModerator && { id: 'mod', label: 'Moderation queue', icon: <Shield size={16} />,
              onSelect: () => navigate('/admin/reports') },
            isModerator && { id: 'modverify', label: 'Verification queue', icon: <Shield size={16} />,
              onSelect: () => navigate('/admin/verification') },
            isModerator && { id: 'modplus', label: 'Plus requests', icon: <Sparkles size={16} />,
              onSelect: () => navigate('/admin/plus') },
            isModerator && { id: 'modresearch', label: 'Research export', icon: <BarChart3 size={16} />,
              onSelect: () => navigate('/admin/research') },
            { id: 'support', label: 'Help and support', icon: <LifeBuoy size={16} />,
              onSelect: () => navigate('/support') },
            { id: 'legal', label: 'Legal',
              onSelect: () => navigate('/legal') },
            { id: 'logout', label: 'Sign out', icon: <LogOut size={16} />, danger: true,
              onSelect: async () => { await logout(); toast.success('Signed out'); navigate('/signin'); } },
          ]}
          trigger={(props) => (
            <button {...props} type="button" className="icon-btn" aria-label="Account menu">
              <Avatar profile={profile} size={30} />
            </button>
          )}
        />
      </div>
    </header>
  );
}

/* ── Mobile tab bar ────────────────────────────────────────────────────────── */

/**
 * Count a visit when someone arrives on a page — or when something new turns
 * up while they are already on it. The ref keeps StrictMode's double effect
 * run, and an event moving backwards (a notification deleted), from counting
 * as a second visit.
 */
function useVisitRecorder(uid, name, active, event) {
  const last = useRef(null);
  useEffect(() => {
    if (!active || !uid) { last.current = null; return; }
    const prev = last.current;
    if (prev && prev.uid === uid && typeof prev.event === typeof event && event <= prev.event) return;
    last.current = { uid, event };
    recordVisit(uid, name, event);
  }, [uid, name, active, event]);
}

const millis = (t) => (typeof t?.toMillis === 'function' ? t.toMillis() : 0);

/**
 * The unread dots on Briefing and Alerts, worked out once for the header and
 * the tab bar together (see lib/navDots for the rule). The briefing's dot also
 * goes when the day is done, and the Alerts dot when nothing is left unread —
 * there is nothing new to point at.
 */
function useNavDots() {
  const { uid } = useAuth();
  const { pathname } = useLocation();
  const stats = useReadingStats(uid);
  const { notifications, unreadCount } = useNotifications(uid);
  const today = londonDay();
  const alertsAt = millis(notifications[0]?.createdAt);

  useVisitRecorder(uid, 'briefing', pathname === '/', today);
  useVisitRecorder(uid, 'alerts', pathname === '/notifications', alertsAt);

  const briefingShown = useDot(uid, 'briefing', today);
  const alertsShown = useDot(uid, 'alerts', alertsAt);
  return {
    briefing: stats !== undefined && stats?.lastDay !== today && briefingShown,
    alerts: unreadCount > 0 && alertsShown,
    unreadCount,
  };
}

function TabBar({ dots }) {
  const { uid } = useAuth();
  const { totalUnread } = useConversations(uid);

  const tab = ({ isActive }) => `tabbar__item${isActive ? ' is-active' : ''}`;

  return (
    <nav className="tabbar" aria-label="Primary">
      <NavLink to="/" end className={tab} aria-label={dots.briefing ? 'Briefing, ready to read' : 'Briefing'}>
        <Newspaper size={21} aria-hidden="true" /><span>Briefing</span>
        {dots.briefing && <span className="tabbar__dot" aria-hidden="true" />}
      </NavLink>
      <NavLink to="/feed" className={tab}>
        <Users size={21} aria-hidden="true" /><span>Feed</span>
      </NavLink>
      <NavLink to="/ask" className={tab}>
        <HelpCircle size={21} aria-hidden="true" /><span>Ask</span>
      </NavLink>
      <NavLink to="/messages" className={tab}>
        <MessageCircle size={21} aria-hidden="true" /><span>Messages</span>
        {totalUnread > 0 && (
          <span className="tabbar__badge" aria-label={`${totalUnread} unread messages`}>
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </NavLink>
      <NavLink to="/notifications" className={tab}
        aria-label={`Alerts${dots.unreadCount ? `, ${dots.unreadCount} unread` : ''}`}>
        <Bell size={21} aria-hidden="true" /><span>Alerts</span>
        {dots.alerts && <span className="tabbar__dot" aria-hidden="true" />}
      </NavLink>
    </nav>
  );
}

/**
 * ErrorBoundary, keyed to the current route.
 *
 * A class component cannot use hooks, and the boundary has to be a class —
 * componentDidCatch has no hook equivalent. So the location is read here and
 * handed down as a prop, which is all getDerivedStateFromProps needs to clear a
 * caught error when the person navigates somewhere else.
 */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

/* ── Offline awareness ─────────────────────────────────────────────────────── */

function OfflineBanner() {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  if (online) return null;
  return (
    <div className="offline-banner" role="status">
      <WifiOff size={14} aria-hidden="true" />
      You are offline. Cached content is shown; anything you post will send when you reconnect.
    </div>
  );
}

/* ── Unverified email ──────────────────────────────────────────────────────── */

/**
 * Says out loud what the security rules are already quietly enforcing.
 *
 * `canWrite()` in firestore.rules requires `email_verified` on the token, so an
 * account that has not clicked its verification link can read the whole app and
 * write nothing. Until this banner existed, that surfaced as every single
 * action — post, comment, like, save, start a conversation, follow — failing
 * with "Missing or insufficient permissions", which tells the user nothing and
 * looks exactly like a broken product.
 *
 * A banner rather than a locked route: the briefing is the reason people are
 * here on day one, and locking them out of it until they find an email is a
 * worse trade than letting them read while nudging them to verify.
 */
function VerifyEmailBanner() {
  const { isSignedIn, emailVerified, user, resendVerification } = useAuth();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!isSignedIn || emailVerified) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await resendVerification();
      setSent(true);
    } catch { /* the button copy below still tells them what to do */ }
    finally { setBusy(false); }
  };

  return (
    <div className="shell-banner" role="status">
      <span>
        Confirm your email{user?.email ? ` (${user.email})` : ''} to post, comment and message.
      </span>
      {sent ? (
        <strong>Sent — check your inbox.</strong>
      ) : (
        <button type="button" onClick={resend} disabled={busy}>
          {busy ? 'Sending…' : 'Resend link'}
        </button>
      )}
    </div>
  );
}

/* ── Changed terms ─────────────────────────────────────────────────────────── */

/**
 * Has this account seen the current version of the Terms and Privacy Policy?
 *
 * Section 12 of the Terms promises to tell people in the app when they change
 * materially, and the consent record (private/settings.consents.termsVersion)
 * says which version each person last saw. When that is older than
 * LEGAL_VERSION, a one-line notice appears until they acknowledge it, and the
 * acknowledgement updates the record — so the answer to "did they know?" is a
 * dated fact rather than an assumption.
 *
 * Only after onboarding: someone mid-signup is agreeing to the current version
 * right there on the consent step.
 */
function useLegalNotice() {
  const { user, profile } = useAuth();
  const uid = user?.uid;
  const ready = !!uid && profile?.onboardingComplete === true;
  const [seen, setSeen] = useState(undefined);   // undefined = not known yet

  useEffect(() => {
    if (!ready) { setSeen(undefined); return undefined; }
    let alive = true;
    getDoc(doc(db, 'users', uid, 'private', 'settings'))
      .then((snap) => { if (alive) setSeen(snap.data()?.consents?.termsVersion ?? ''); })
      // Unknown stays unknown: a failed read must not nag, and must not
      // record an acknowledgement that never happened.
      .catch(() => { if (alive) setSeen(undefined); });
    return () => { alive = false; };
  }, [ready, uid]);

  const visible = ready && seen !== undefined && seen < LEGAL_VERSION;

  const acknowledge = async () => {
    const previous = seen;
    setSeen(LEGAL_VERSION);
    try {
      await setDoc(doc(db, 'users', uid, 'private', 'settings'), {
        consents: { termsVersion: LEGAL_VERSION, acceptedAt: serverTimestamp() },
      }, { merge: true });
    } catch {
      setSeen(previous);
    }
  };

  return { visible, acknowledge };
}

function LegalNoticeBanner({ onAcknowledge }) {
  return (
    <div className="shell-banner" role="status">
      <span>Our Terms and Privacy Policy have changed.</span>
      <Link to="/legal/privacy" className="shell-banner__link">Read them</Link>
      <button type="button" onClick={onAcknowledge}>OK</button>
    </div>
  );
}

/**
 * Robots rule and canonical URL for the current route (lib/pageMeta.js).
 *
 * Rendered before the routes so its effect runs first on each navigation, and
 * a page that sets its own title and description does so afterwards. Pages
 * that set nothing are reset to the defaults here, so a title never lingers
 * from the page before.
 */
function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    applyRouteRules(pathname);
    setPageMeta();
  }, [pathname]);
  return null;
}

/** Restore scroll to the top on navigation — SPAs otherwise keep the old offset. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

/* ── Shell ─────────────────────────────────────────────────────────────────── */

function Shell({ children }) {
  // The banner's own render condition, lifted so the shell can tighten the
  // header while it is up. Duplicating the test beats having the banner reach
  // up and mutate a parent class.
  const { isSignedIn, emailVerified } = useAuth();
  const legal = useLegalNotice();
  const verifyBanner = isSignedIn && !emailVerified;
  const banners = (verifyBanner ? 1 : 0) + (legal.visible ? 1 : 0);
  const dots = useNavDots();

  return (
    <div className={`shell${banners ? ' has-banner' : ''}${banners > 1 ? ' has-two-banners' : ''}`}>
      {/* Skip link — the first thing a keyboard user hits, and a WCAG 2.4.1
          requirement the original had no equivalent of. */}
      <a href="#main" className="sr-only sr-only-focusable">Skip to main content</a>
      <OfflineBanner />
      <VerifyEmailBanner />
      {legal.visible && <LegalNoticeBanner onAcknowledge={legal.acknowledge} />}
      <Header dots={dots} />
      <main id="main" tabIndex={-1}>{children}</main>
      <TabBar dots={dots} />
    </div>
  );
}

/* ── Routes ────────────────────────────────────────────────────────────────── */

function AppRoutes() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/:doc" element={<Legal />} />
        {/* Outside RequireAuth on purpose. The most common reason to want
            support is being unable to sign in, and a support page behind a
            login is a locked door with a help sign on it. */}
        <Route path="/support" element={<Support />} />

        {/* Post-signup, pre-profile */}
        <Route path="/welcome" element={<Onboarding />} />
        <Route path="/verify" element={<VerifyEmail />} />

        {/* Authenticated app */}
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Shell>
                {/* resetKey is what lets a caught error clear on navigation.
                    Without it, React keeps the error in state and one page
                    throwing makes every later page show the error screen for
                    the rest of the session. */}
                <RouteErrorBoundary>
                  <Routes>
                    <Route index element={<Briefing />} />
                    <Route path="feed" element={<Social />} />
                    <Route path="post/:postId" element={<PostDetail />} />
                    <Route path="u/:handle" element={<Profile />} />
                    <Route path="u/id/:uid" element={<ProfileById />} />
                    <Route path="tag/:tag" element={<Social />} />
                    <Route path="messages" element={<Messages />} />
                    <Route path="messages/:convId" element={<Messages />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="library" element={<Library />} />
                    <Route path="search" element={<Search />} />
                    <Route path="ask" element={<Ask />} />
                    {/* The long-form editor. `write/:postId` edits a published
                        article; the bare route starts a new one. Both sit
                        inside the shell so the nav stays put. */}
                    <Route path="write" element={<WriteArticle />} />
                    <Route path="write/:postId" element={<WriteArticle />} />
                    <Route path="groups" element={<Groups />} />
                    <Route path="groups/:groupId" element={<GroupDetail />} />
                    <Route path="events" element={<Events />} />
                    <Route path="settings" element={<Settings />} />
                    {/* Explicit route, above the :section catch-all, so
                        /settings/verification is not swallowed by it. */}
                    <Route path="settings/verification" element={<Verify />} />
                    <Route path="settings/:section" element={<Settings />} />
                    <Route path="admin/verification" element={<AdminVerify />} />
                    <Route path="admin/reports" element={<AdminReports />} />
                    <Route path="admin/plus" element={<AdminPlus />} />
                    <Route path="admin/research" element={<AdminResearch />} />
                    <Route path="report" element={<Report />} />
                    {/* Catch-all inside the shell, so a bad link keeps the nav. */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </RouteErrorBoundary>
              </Shell>
            </RequireAuth>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <GlobalStyles />
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <RouteMeta />
            <ScrollToTop />
            <AppRoutes />
            {/* Outside the routes so the choice is asked for, and honoured, on
                every page — the public ones included. */}
            <ConsentManager />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
